/* eslint-disable no-alert */
/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import rtkRequestStatusCodes from "../../lib/const/rtkRequestStatusCodes";
import {
  getTasksRoute,
  getTasksTypesRoute,
  getSubtasksRoute,
  fetchTaskStatusRoute,
  fetchTaskByIdRoute,
} from "../../lib/routes/tasks-manager";
import { getUserByIdRoute } from "../../lib/routes/security";

const initialState = {
  tasks: {},
  tasksLoading: rtkRequestStatusCodes.idle,
  task: [],
  taskLoading: rtkRequestStatusCodes.idle,
  taskTypes: [],
  taskTypesLoading: rtkRequestStatusCodes.idle,
  taskStatus: {},
  taskStatusLoading: rtkRequestStatusCodes.idle,
  subtasks: {},
  subtasksLoading: rtkRequestStatusCodes.idle,
  initiators: [],
  initiatorsLoading: rtkRequestStatusCodes.idle,
};

const fetchTasks = createAsyncThunk("tasks/fetchTasks", async ({ queryParams }) => {
  const url = getTasksRoute({ sort: ["id,desc"], ...queryParams });
  const { data } = await axios(url, { params: { ...queryParams } });
  return data;
});

const fetchTaskById = createAsyncThunk("tasks/fetchTasksById", async ({ taskId, includeChildren }) => {
  const url = fetchTaskByIdRoute({ taskId, includeChildren });
  const { data } = await axios(url);
  return data;
});

const fetchSubtasks = createAsyncThunk("tasks/fetchSubtasks", async ({ taskId, status, page }) => {
  const { data } = await axios(getSubtasksRoute(taskId, status, { page }));
  return data;
});

const fetchTaskStatus = createAsyncThunk("tasks/fetchTaskStatus", async ({ taskId }) => {
  const url = fetchTaskStatusRoute({ taskId });
  const { data } = await axios(url);
  return data;
});

const fetchTaskTypes = createAsyncThunk("tasks/fetchTasksTypes", async () => {
  const url = getTasksTypesRoute();
  const { data } = await axios(url);
  return data;
});

const fetchInitiators = createAsyncThunk("tasks/fetchInitiators", async ({ initiatorsIds }) => {
  const response = await Promise.allSettled(
    initiatorsIds.map((item) => axios.get(getUserByIdRoute(item)))
  );
  return response.filter((item) => item.status === "fulfilled").map((item) => item.value.data);
});

const slice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    changeTaskStatus: {
      reducer: (state, action) => {
        state.tasks.content = state.tasks.content.map((task) => {
          if (task.id === action.payload.taskId) {
            return {
              ...task,
              statusId: action.payload.statusId,
            };
          }
          return task;
        });
      },
    },
    clearTask: {
      reducer: (state) => {
        state.task = {};
        state.taskLoading = rtkRequestStatusCodes.idle;
      },
    },
  },
  extraReducers: {
    [fetchTasks.pending]: (state) => {
      state.tasksLoading = rtkRequestStatusCodes.pending;
    },
    [fetchTasks.fulfilled]: (state, { payload }) => {
      state.tasks = payload;
      state.tasksLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchTasks.rejected]: (state) => {
      state.tasksLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchTaskById.pending]: (state) => {
      state.taskLoading = rtkRequestStatusCodes.pending;
    },
    [fetchTaskById.fulfilled]: (state, { payload }) => {
      state.task = payload;
      state.taskLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchTaskById.rejected]: (state) => {
      state.taskLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchTaskTypes.fulfilled]: (state, { payload }) => {
      state.taskTypes = {
        ...payload,
        content: [...payload.content].sort((a, b) => a.title.localeCompare(b.title)),
      };
      state.taskTypesLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchTaskTypes.pending]: (state) => {
      state.taskTypesLoading = rtkRequestStatusCodes.pending;
    },
    [fetchTaskTypes.rejected]: (state) => {
      state.taskTypesLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchSubtasks.pending]: (state) => {
      state.subtasksLoading = rtkRequestStatusCodes.pending;
    },
    [fetchSubtasks.rejected]: (state) => {
      state.subtasksLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchSubtasks.fulfilled]: (state, { payload }) => {
      state.subtasks = payload;
      state.subtasksLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchTaskStatus.pending]: (state) => {
      state.taskStatusLoading = rtkRequestStatusCodes.pending;
    },
    [fetchTaskStatus.rejected]: (state) => {
      state.taskStatusLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchTaskStatus.fulfilled]: (state, { payload }) => {
      state.taskStatus = payload;
      state.taskStatusLoading = rtkRequestStatusCodes.fulfilled;
    },
    [fetchInitiators.pending]: (state) => {
      state.initiatorsLoading = rtkRequestStatusCodes.pending;
    },
    [fetchInitiators.rejected]: (state) => {
      state.initiatorsLoading = rtkRequestStatusCodes.rejected;
    },
    [fetchInitiators.fulfilled]: (state, { payload }) => {
      state.initiators = payload;
      state.initiatorsLoading = rtkRequestStatusCodes.fulfilled;
    },
  },
});

const { actions, reducer } = slice;
const { changeTaskStatus, clearTask } = actions;

export {
  actions,
  fetchTasks,
  fetchTaskTypes,
  fetchSubtasks,
  fetchTaskStatus,
  fetchInitiators,
  fetchTaskById,
  changeTaskStatus,
  clearTask,
};
export default reducer;
